import { useEffect, useMemo, useState } from 'react';

import type { CategoryCount, PipeItem } from './integrations-search-types';

const PIPES_JSON_URL =
	'https://bitbucket.org/bitbucketpipelines/official-pipes/raw/master/pipes.prod.json';

const buildCategoryId = (label: string) => label.toLowerCase().replace(/\s+/g, '');

export const usePipesData = (jsonUrl?: string) => {
	const [items, setItems] = useState<PipeItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		let cancelled = false;

		const run = async () => {
			setLoading(true);
			setError(null);

			try {
				const res = await fetch(jsonUrl || PIPES_JSON_URL);

				if (!res.ok) {
					throw new Error(`Failed to fetch pipes data: ${res.status}`);
				}

				const data: PipeItem[] = await res.json();

				if (!cancelled) {
					setItems(data);
				}
			} catch (err) {
				if (!cancelled) {
					setError(err instanceof Error ? err : new Error('Unknown error fetching pipes data'));
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		run();

		return () => {
			cancelled = true;
		};
	}, [jsonUrl]);

	const categories = useMemo<CategoryCount[]>(() => {
		const counts = new Map<string, number>();

		items.forEach((item) => {
			const label = item.category || 'Other';
			counts.set(label, (counts.get(label) ?? 0) + 1);
		});

		return Array.from(counts.entries())
			.map(([label, count]) => ({
				id: buildCategoryId(label),
				label,
				count,
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [items]);

	return { items, categories, loading, error };
};
